import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, IconButton, Paper, Popover, Tab, Tabs,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BarChartIcon from '@mui/icons-material/BarChart';
import RepeatIcon from '@mui/icons-material/Repeat';
import FlagIcon from '@mui/icons-material/Flag';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useHistory, useParams } from 'react-router-dom';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { ProjectsService } from '../../services/dao/projects-service';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const projectsService = new ProjectsService();
const fmtMoney = (v) => `$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const COLORS = ['#1F2F42', '#C89E57', '#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#EF4444', '#14B8A6', '#F97316', '#06B6D4'];

export default function ProjectDashboardScreen() {
    const { projectId } = useParams();
    const history = useHistory();
    const [findocs, setFindocs] = useState([]);
    const [projectName, setProjectName] = useState('');
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);
    const [catSampleAnchor, setCatSampleAnchor] = useState(null);
    const [catSampleName, setCatSampleName] = useState('');
    const [catSampleItems, setCatSampleItems] = useState([]);

    useEffect(() => {
        Promise.all([
            projectsService.getProjectContents(projectId),
            projectsService.getProjects(),
        ]).then(([contents, projectList]) => {
            if (contents?.body) setFindocs(contents.body.findocs || []);
            if (projectList?.body) {
                const p = projectList.body.find(p => p._id === projectId);
                setProjectName(p?.name || 'Project');
            }
            setLoading(false);
        });
    }, [projectId]);

    // Extract all line items from all findocs
    const allLineItems = useMemo(() =>
        findocs.flatMap(f => (f.lineItems || []).map(li => ({ ...li, institutionName: f.institutionName }))),
        [findocs]
    );

    // Category spending: group by category, sum amounts (debit = spending)
    const categoryData = useMemo(() => {
        const map = {};
        allLineItems.forEach(li => {
            const cat = li.category || 'Uncategorized';
            const amt = Math.abs(li.amount || 0);
            if (amt > 0 && li.transactionType === 'debit') map[cat] = (map[cat] || 0) + amt;
        });
        return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10);
    }, [allLineItems]);

    // Purpose spending: group by purpose, sum debit amounts
    const PURPOSE_LABELS = {
        'tax:deductible': 'Tax Deductible', 'tax:non-deductible': 'Tax Non-Deductible',
        'business:operating': 'Business Operating', 'business:travel': 'Business Travel',
        'business:meals': 'Business Meals', 'business:equipment': 'Business Equipment',
        'business:contractor': 'Business Contractor', 'personal:essential': 'Personal Essential',
        'personal:discretionary': 'Personal Discretionary', 'personal:savings': 'Personal Savings',
    };
    const purposeData = useMemo(() => {
        const map = {};
        allLineItems.forEach(li => {
            if (!li.purpose) return;
            const amt = Math.abs(li.amount || 0);
            if (amt > 0 && li.transactionType === 'debit') map[li.purpose] = (map[li.purpose] || 0) + amt;
        });
        return Object.entries(map).sort((a, b) => b[1] - a[1]);
    }, [allLineItems]);

    // Subscriptions: find descriptions that appear 2+ times with similar amounts, or AI-tagged
    const subscriptions = useMemo(() => {
        const byDesc = {};
        allLineItems.forEach(li => {
            if (!li.description || li.amount == null) return;
            const key = li.description.toLowerCase().replace(/\s+/g, ' ').trim();
            const amt = Math.abs(li.amount);
            if (!byDesc[key]) byDesc[key] = { description: li.description, amounts: [], count: 0, aiTagged: false };
            byDesc[key].amounts.push(amt);
            byDesc[key].count++;
            if (li.isSubscription) byDesc[key].aiTagged = true;
        });
        return Object.values(byDesc)
            .filter(s => {
                if (s.aiTagged) return true;
                if (s.count < 2) return false;
                const avg = s.amounts.reduce((a, b) => a + b, 0) / s.amounts.length;
                return avg > 0 && s.amounts.every(a => Math.abs(a - avg) / avg < 0.15);
            })
            .map(s => ({
                description: s.description,
                avgAmount: s.amounts.reduce((a, b) => a + b, 0) / s.amounts.length,
                occurrences: s.count,
                totalSpent: s.amounts.reduce((a, b) => a + b, 0),
            }))
            .sort((a, b) => b.totalSpent - a.totalSpent);
    }, [allLineItems]);

    const chartData = {
        labels: categoryData.map(([cat]) => cat.length > 20 ? cat.slice(0, 20) + '...' : cat),
        datasets: [{
            label: 'Spending',
            data: categoryData.map(([, amt]) => amt),
            backgroundColor: categoryData.map((_, i) => COLORS[i % COLORS.length]),
            borderRadius: 6,
        }],
    };

    const PURPOSE_COLORS_MAP = {
        'tax:deductible': '#059669', 'tax:non-deductible': '#6B7280',
        'business:operating': '#2563EB', 'business:travel': '#7C3AED', 'business:meals': '#D97706',
        'business:equipment': '#0891B2', 'business:contractor': '#4F46E5',
        'personal:essential': '#475569', 'personal:discretionary': '#9333EA', 'personal:savings': '#10B981',
    };

    const purposeChartData = {
        labels: purposeData.map(([p]) => (PURPOSE_LABELS[p] || p).length > 22 ? (PURPOSE_LABELS[p] || p).slice(0, 22) + '...' : (PURPOSE_LABELS[p] || p)),
        datasets: [{
            label: 'Spending',
            data: purposeData.map(([, amt]) => amt),
            backgroundColor: purposeData.map(([p]) => PURPOSE_COLORS_MAP[p] || '#94A3B8'),
            borderRadius: 6,
        }],
    };

    const chartOptions = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => fmtMoney(ctx.raw) } } },
        scales: {
            x: { ticks: { callback: v => '$' + (v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v.toFixed(0)) }, grid: { display: false } },
            y: { grid: { display: false } },
        },
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 4, maxWidth: 1000, mx: 'auto' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/projects/' + projectId)}
                sx={{ mb: 3, color: 'text.secondary', fontWeight: 600, borderRadius: 2, textTransform: 'none' }}>
                Back to {projectName}
            </Button>

            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>{projectName} — Insights</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                {findocs.length} statement{findocs.length !== 1 ? 's' : ''} · {allLineItems.length} transactions
            </Typography>

            {allLineItems.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10 }}>
                    <BarChartIcon sx={{ fontSize: 56, color: 'grey.300', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: 'text.secondary' }}>No transaction data yet</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Upload statements to this project to see insights</Typography>
                </Box>
            ) : (
                <>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3,
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9rem' } }}>
                        <Tab label="Category Spending" icon={<BarChartIcon sx={{ fontSize: '1.1rem' }} />} iconPosition="start" />
                        <Tab label="Purpose Spending" icon={<FlagIcon sx={{ fontSize: '1.1rem' }} />} iconPosition="start" />
                        <Tab label={`Subscriptions (${subscriptions.length})`} icon={<RepeatIcon sx={{ fontSize: '1.1rem' }} />} iconPosition="start" />
                    </Tabs>

                    {tab === 0 && (
                        <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Top Spending Categories</Typography>
                            {categoryData.length > 0 ? (
                                <Box sx={{ height: Math.max(250, categoryData.length * 40) }}>
                                    <Bar data={chartData} options={chartOptions} />
                                </Box>
                            ) : (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>No category data available</Typography>
                            )}
                            {/* Summary chips */}
                            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                {categoryData.slice(0, 5).map(([cat, amt], i) => (
                                    <Box key={cat} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                        <Chip label={`${cat}: ${fmtMoney(amt)}`} size="small"
                                            sx={{ fontWeight: 600, background: COLORS[i] + '18', color: COLORS[i] }} />
                                        <IconButton size="small" sx={{ p: 0.25, ml: -0.5 }} onClick={(e) => {
                                            setCatSampleAnchor(e.currentTarget);
                                            setCatSampleName(cat);
                                            setCatSampleItems(allLineItems.filter(li => (li.category || 'Uncategorized') === cat).slice(0, 5));
                                        }}>
                                            <InfoOutlinedIcon sx={{ fontSize: '0.8rem', color: COLORS[i] }} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                            <Popover open={Boolean(catSampleAnchor)} anchorEl={catSampleAnchor}
                                onClose={() => setCatSampleAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                                <Box sx={{ p: 2, maxWidth: 300, minWidth: 200 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{catSampleName} — Samples</Typography>
                                    {catSampleItems.length === 0 ? (
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>No items</Typography>
                                    ) : catSampleItems.map((li, i) => (
                                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
                                            <Typography variant="caption" noWrap sx={{ flex: 1, mr: 1 }}>{li.description || '—'}</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 600, flexShrink: 0 }}>{fmtMoney(li.amount)}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Popover>
                        </Paper>
                    )}

                    {tab === 1 && (
                        <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Spending by Purpose</Typography>
                            {purposeData.length > 0 ? (
                                <Box sx={{ height: Math.max(250, purposeData.length * 40) }}>
                                    <Bar data={purposeChartData} options={chartOptions} />
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 6 }}>
                                    <FlagIcon sx={{ fontSize: 40, color: 'grey.300', mb: 1 }} />
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>No purpose data yet</Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Reanalyze statements to see spending by purpose</Typography>
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                                {purposeData.map(([p, amt]) => (
                                    <Chip key={p} label={`${PURPOSE_LABELS[p] || p}: ${fmtMoney(amt)}`} size="small"
                                        sx={{ fontWeight: 600, background: (PURPOSE_COLORS_MAP[p] || '#94A3B8') + '18', color: PURPOSE_COLORS_MAP[p] || '#94A3B8' }} />
                                ))}
                            </Box>
                        </Paper>
                    )}

                    {tab === 2 && (
                        <Paper variant="outlined" sx={{ borderRadius: 3 }}>
                            {subscriptions.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 6 }}>
                                    <RepeatIcon sx={{ fontSize: 40, color: 'grey.300', mb: 1 }} />
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>No recurring charges detected</Typography>
                                </Box>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' } }}>
                                                <TableCell>Description</TableCell>
                                                <TableCell align="right">Avg Amount</TableCell>
                                                <TableCell align="center">Occurrences</TableCell>
                                                <TableCell align="right">Total Spent</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {subscriptions.map((s, i) => (
                                                <TableRow key={i} sx={{ '&:nth-of-type(even)': { background: '#F8FAFC' } }}>
                                                    <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem' }}>{s.description}</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#DC2626' }}>{fmtMoney(s.avgAmount)}</TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={`${s.occurrences}x`} size="small" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{fmtMoney(s.totalSpent)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>
                    )}
                </>
            )}
        </Box>
    );
}
